import { DEFAULT_LOCALE, isLocale, type Locale } from "@khazzin/i18n";
import { formatMoney, minorUnits, parseMoney } from "@khazzin/i18n/money";
import { Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { Icon } from "../../icons/Icon";
import { cx, DISABLED, TOUCH_TARGET } from "../../styles";

/**
 * An amount, held as an integer count of minor units and never as a float.
 *
 * The value in and out is `amountMinor` — 1250 for $12.50 — and the text in the
 * box is the only place a decimal point exists. That is the whole design, and
 * it is what makes the round trip safe: `lib/money` parses digit strings and
 * concatenates them, so `12.5` becomes 1250 by string arithmetic rather than by
 * `12.5 * 100`, which is 1250.0000000000002 in every engine there is.
 *
 * **Parsed on blur and on Enter, not on every keystroke.** Somebody typing
 * `12.` has not typed an invalid amount, they have typed half of one, and a
 * field that flashed red mid-number would be unusable. The text is theirs until
 * they leave it.
 *
 * **The currency cell is locked by default**, because a tenant has one currency
 * (`10/00` §4) and an order line cannot be in a different one from its order.
 * The lock icon says so, rather than leaving somebody to discover it by
 * clicking.
 */

export interface MoneyInputProps {
	/** The amount in minor units, or null when the field is empty. */
	value: number | null;
	currency: string;
	onChange: (amountMinor: number | null) => void;
	/** The currency cannot be changed here. True for almost every field. */
	locked?: boolean;
	/** Refuse anything below this, in minor units. Usually 0. */
	min?: number;
	/** Shown when the text cannot be read as an amount. */
	invalidLabel?: string;
	/** Shown when the amount is below `min`. */
	minLabel?: string;
	/** Reports a problem with what was typed, so a Field can render it. */
	onValidityChange?: (error: string | null) => void;
	disabled?: boolean;
	placeholder?: string;
	id?: string;
	name?: string;
	"aria-describedby"?: string | undefined;
	"aria-invalid"?: true | undefined;
	/** For a field with no visible label of its own, such as a cell in a table of amounts. */
	"aria-label"?: string | undefined;
	className?: string;
	/**
	 * Which locale's separators to accept. Defaults to the document's.
	 *
	 * Read off `<html lang>` rather than out of a React context, and for the
	 * same two reasons the API client does it: the document's language is
	 * already the single statement of what the person is looking at, so the
	 * parser cannot disagree with the screen — and `ui/` stays a package that
	 * depends on nothing in `app/`, which is what lets it be extracted later.
	 */
	locale?: Locale;
}

/** Digits, one separator, an optional sign — everything a person can legitimately type. */
const TYPEABLE = /^[-+]?[\d\s.,٠-٩۰-۹٫٬]*$/;

export function MoneyInput({
	value,
	currency,
	onChange,
	locked = true,
	min,
	invalidLabel = "Enter an amount",
	minLabel = "Too low",
	onValidityChange,
	disabled = false,
	placeholder,
	id,
	name,
	className,
	"aria-describedby": describedBy,
	"aria-invalid": invalid,
	"aria-label": ariaLabel,
	locale: override,
}: MoneyInputProps) {
	const locale = override ?? documentLocale();
	const [text, setText] = useState(() => toText(value, currency));

	// Adopt a value the form set from outside — a reset, or a record loading in
	// after the field mounted. Guarded on the parse so it does not fight the
	// person typing: re-rendering while they are mid-number must not rewrite
	// what is in the box.
	useEffect(() => {
		setText((current) =>
			parseMoney(current, currency, locale) === value ? current : toText(value, currency),
		);
	}, [value, currency, locale]);

	function commit() {
		const trimmed = text.trim();
		if (trimmed === "") {
			onChange(null);
			onValidityChange?.(null);
			return;
		}

		const minorAmount = parseMoney(trimmed, currency, locale);
		if (minorAmount === null) {
			// The text stays as typed. Clearing it would throw away what somebody
			// entered in order to tell them it was wrong.
			onValidityChange?.(invalidLabel);
			return;
		}
		if (min !== undefined && minorAmount < min) {
			onValidityChange?.(minLabel);
			return;
		}

		onChange(minorAmount);
		onValidityChange?.(null);
		// Normalised on the way out: `12.5` becomes `12.50`, so a column of
		// amounts lines up and the person can see the field was understood.
		setText(toText(minorAmount, currency));
	}

	return (
		<div className="flex flex-col gap-1">
			<div
				className={cx(
					"flex h-9 items-center overflow-hidden rounded-md border border-border-strong bg-bg-surface",
					"focus-within:ring-3 focus-within:ring-ring-halo focus-within:border-ring",
					"has-[input[aria-invalid]]:border-danger-border has-[input[aria-invalid]]:ring-3 has-[input[aria-invalid]]:ring-danger-bg",
					TOUCH_TARGET,
					DISABLED,
					className,
				)}
			>
				<span
					// Decorative: the currency is on the hint and in the value the API
					// receives, and announcing "USD" before every amount in a table of
					// prices is noise.
					aria-hidden="true"
					className="flex h-full items-center gap-1 border-e border-border px-2.5 text-[13px] text-fg-subtle"
				>
					{currency}
					{locked && <Icon as={Lock} size={11} />}
				</span>

				<input
					id={id}
					name={name}
					value={text}
					disabled={disabled}
					placeholder={placeholder}
					inputMode="decimal"
					aria-describedby={describedBy}
					aria-invalid={invalid}
					aria-label={ariaLabel}
					onChange={(event) => {
						// Letters are refused as they are typed rather than reported on
						// blur: there is no amount they could be part of, and a scanner
						// or a paste that put them there is worth stopping at once.
						if (TYPEABLE.test(event.target.value)) {
							setText(event.target.value);
						}
					}}
					onBlur={commit}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							commit();
						}
					}}
					className={cx(
						"h-full min-w-0 flex-1 bg-transparent px-2.5 text-[13px] text-fg outline-none",
						// End-aligned and tabular, so a column of prices lines up on the
						// decimal point instead of shimmering as digits change width.
						"text-end [font-variant-numeric:tabular-nums]",
						"placeholder:text-fg-subtle disabled:cursor-not-allowed disabled:text-fg-subtle",
					)}
				/>
			</div>
		</div>
	);
}

/** The page's language, or English when it says nothing this build knows. */
function documentLocale(): Locale {
	const lang = typeof document === "undefined" ? "" : document.documentElement.lang;
	return isLocale(lang) ? lang : DEFAULT_LOCALE;
}

/** The editable text for an amount. Empty for null, so the field starts blank. */
function toText(amountMinor: number | null, currency: string): string {
	if (amountMinor === null) {
		return "";
	}
	// Plain digits, not `formatMoney`: the box holds a number somebody edits,
	// and a currency symbol or a group separator inside it would be re-parsed
	// on every keystroke.
	const digits = minorUnits(currency);
	const negative = amountMinor < 0;
	const raw = String(Math.abs(amountMinor)).padStart(digits + 1, "0");
	const whole = raw.slice(0, raw.length - digits);
	const fraction = digits === 0 ? "" : `.${raw.slice(raw.length - digits)}`;
	return `${negative ? "-" : ""}${whole}${fraction}`;
}

export { formatMoney };
